import type { NodeType } from '../render/context/node';

export class Rect {
  x: number;
  y: number;
  w: number;
  h: number;
  nodes: NodeType[] = [];

  constructor(x, y, w, h) {
    if (w < 0) {
      x = x + w;
      w = -w;
    }
    if (h < 0) {
      y = y + h;
      h = -h;
    }
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
  }

  static zero = new Rect(0, 0, 0, 0);

  static from({ x, y, w, h }) {
    return new Rect(x, y, w, h);
  }

  static fromPoints(p1, p2) {
    return new Rect(p1.x, p1.y, p2.x - p1.x, p2.y - p1.y);
  }

  get area() {
    return this.w * this.h;
  }

  get center() {
    return {
      x: this.x + this.w / 2,
      y: this.y + this.h / 2,
    };
  }

  get topLeft() {
    return {
      x: this.x,
      y: this.y,
    };
  }

  get topRight() {
    return {
      x: this.x + this.w,
      y: this.y,
    };
  }

  get bottomLeft() {
    return {
      x: this.x,
      y: this.y + this.h,
    };
  }

  get bottomRight() {
    return {
      x: this.x + this.w,
      y: this.y + this.h,
    };
  }

  get circle() {
    const radius = Math.min(this.w, this.h) / 2;
    return new Circle({
      x: this.x + this.w / 2,
      y: this.y + this.h / 2,
      radius,
    });
  }

  inflate(value) {
    this.x -= value;
    this.y -= value;
    this.w += value * 2;
    this.h += value * 2;
    return this;
  }

  round() {
    this.x = Math.round(this.x);
    this.y = Math.round(this.y);
    this.w = Math.round(this.w);
    this.h = Math.round(this.h);
    return this;
  }

  contains(x, y) {
    return x >= this.x && x <= this.x + this.w && y >= this.y && y <= this.y + this.h;
  }

  containsRect(rect) {
    return this.contains(rect.x, rect.y) && this.contains(rect.x + rect.w, rect.y + rect.h);
  }

  containsRectPartly(rect) {
    return (
      this.contains(rect.x, rect.y) ||
      this.contains(rect.x + rect.w, rect.y) ||
      this.contains(rect.x, rect.y + rect.h) ||
      this.contains(rect.x + rect.w, rect.y + rect.h)
    );
  }

  intersection(rect) {
    const x1 = Math.max(this.x, rect.x);
    const y1 = Math.max(this.y, rect.y);
    const x2 = Math.min(this.x + this.w, rect.x + rect.w);
    const y2 = Math.min(this.y + this.h, rect.y + rect.h);

    if (x2 >= x1 && y2 >= y1) {
      const intersectionWidth = x2 - x1;
      const intersectionHeight = y2 - y1;
      const rect = new Rect(x1, y1, intersectionWidth, intersectionHeight);
      if (rect.area > 0) {
        return rect;
      }
    }

    return null;
  }

  expand(x, y) {
    const minX = Math.min(this.x, x);
    const minY = Math.min(this.y, y);
    const maxX = Math.max(this.x + this.w, x);
    const maxY = Math.max(this.y + this.h, y);
    this.x = minX;
    this.y = minY;
    this.w = maxX - minX;
    this.h = maxY - minY;
  }

  expandRect(rect) {
    this.expand(rect.x, rect.y);
    this.expand(rect.x + rect.w, rect.y + rect.h);
  }

  translate(x, y) {
    this.x += x;
    this.y += y;
  }

  transform(scale, translateX, translateY) {
    return new Region({
      topLeft: new Point(this.topLeft.x * scale + translateX, this.topLeft.y * scale + translateY),
      topRight: new Point(
        this.topRight.x * scale + translateX,
        this.topRight.y * scale + translateY
      ),
      bottomRight: new Point(
        this.bottomRight.x * scale + translateX,
        this.bottomRight.y * scale + translateY
      ),
      bottomLeft: new Point(
        this.bottomLeft.x * scale + translateX,
        this.bottomLeft.y * scale + translateY
      ),
    }).rect;
  }

  transformMatrix(matrix) {
    // 仅支持 6 元素仿射矩阵
    if (!Array.isArray(matrix) || matrix.length !== 6) return this.clone();

    // 获取四个角点
    const points = [
      { x: this.x, y: this.y }, // topLeft
      { x: this.x + this.w, y: this.y }, // topRight
      { x: this.x + this.w, y: this.y + this.h }, // bottomRight
      { x: this.x, y: this.y + this.h }, // bottomLeft
    ];

    // 变换每个点
    const transformed = points.map((pt) => {
      const [a, b, c, d, e, f] = matrix;
      return {
        x: a * pt.x + c * pt.y + e,
        y: b * pt.x + d * pt.y + f,
      };
    });

    // 计算新外接矩形
    const xs = transformed.map((p) => p.x);
    const ys = transformed.map((p) => p.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return new Rect(minX, minY, maxX - minX, maxY - minY);
  }

  clone() {
    return new Rect(this.x, this.y, this.w, this.h);
  }
}

export class Point {
  x: number;
  y: number;
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
}

class Circle {
  x: number;
  y: number;
  radius: number;

  constructor({ x, y, radius }) {
    this.x = x;
    this.y = y;
    this.radius = radius;
  }

  contains(x, y) {
    const dx = x - this.x;
    const dy = y - this.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance <= this.radius;
  }
}

export class Region {
  topLeft: Point;
  topRight: Point;
  bottomRight: Point;
  bottomLeft: Point;

  constructor({ topLeft, topRight, bottomRight, bottomLeft }) {
    this.topLeft = topLeft;
    this.topRight = topRight;
    this.bottomRight = bottomRight;
    this.bottomLeft = bottomLeft;
  }

  get angle() {
    const dx = this.topRight.x - this.topLeft.x;
    const dy = this.topRight.y - this.topLeft.y;
    return Math.atan2(dy, dx) * (180 / Math.PI);
  }

  get w() {
    return Math.sqrt(
      Math.pow(this.topRight.x - this.topLeft.x, 2) + Math.pow(this.topRight.y - this.topLeft.y, 2)
    );
  }

  get h() {
    return Math.sqrt(
      Math.pow(this.bottomRight.x - this.topLeft.x, 2) +
        Math.pow(this.bottomRight.y - this.topLeft.y, 2)
    );
  }

  set w(value) {
    const angleRad = this.angle * (Math.PI / 180);
    const newTopRightX = this.topLeft.x + value * Math.cos(angleRad);
    const newTopRightY = this.topLeft.y + value * Math.sin(angleRad);
    const newBottomRightX = this.bottomLeft.x + value * Math.cos(angleRad);
    const newBottomRightY = this.bottomLeft.y + value * Math.sin(angleRad);

    this.topRight = new Point(newTopRightX, newTopRightY);
    this.bottomRight = new Point(newBottomRightX, newBottomRightY);
  }

  set h(value) {
    const angleRad = this.angle * (Math.PI / 180);
    const newBottomLeftX = this.topLeft.x + value * Math.sin(angleRad);
    const newBottomLeftY = this.topLeft.y + value * Math.cos(angleRad);
    const newBottomRightX = this.topRight.x + value * Math.sin(angleRad);
    const newBottomRightY = this.topRight.y + value * Math.cos(angleRad);

    this.bottomLeft = new Point(newBottomLeftX, newBottomLeftY);
    this.bottomRight = new Point(newBottomRightX, newBottomRightY);
  }

  get rect() {
    const minX = Math.min(this.topLeft.x, this.topRight.x, this.bottomRight.x, this.bottomLeft.x);
    const minY = Math.min(this.topLeft.y, this.topRight.y, this.bottomRight.y, this.bottomLeft.y);
    const maxX = Math.max(this.topLeft.x, this.topRight.x, this.bottomRight.x, this.bottomLeft.x);
    const maxY = Math.max(this.topLeft.y, this.topRight.y, this.bottomRight.y, this.bottomLeft.y);
    return new Rect(minX, minY, maxX - minX, maxY - minY);
  }

  static fromRect(rect) {
    const topLeft = new Point(rect.x, rect.y);
    const topRight = new Point(rect.x + rect.w, rect.y);
    const bottomRight = new Point(rect.x + rect.w, rect.y + rect.h);
    const bottomLeft = new Point(rect.x, rect.y + rect.h);
    return new Region({ topLeft, topRight, bottomRight, bottomLeft });
  }

  get center() {
    const centerX = (this.topLeft.x + this.topRight.x + this.bottomRight.x + this.bottomLeft.x) / 4;
    const centerY = (this.topLeft.y + this.topRight.y + this.bottomRight.y + this.bottomLeft.y) / 4;
    return new Point(centerX, centerY);
  }

  inflate(padding) {
    const centerX = this.center.x;
    const centerY = this.center.y;

    const inflatePoint = (point) => {
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const angle = Math.atan2(dy, dx);
      const newDistance = distance + padding;
      const newX = centerX + newDistance * Math.cos(angle);
      const newY = centerY + newDistance * Math.sin(angle);
      return new Point(newX, newY);
    };

    const newTopLeft = inflatePoint(this.topLeft);
    const newTopRight = inflatePoint(this.topRight);
    const newBottomRight = inflatePoint(this.bottomRight);
    const newBottomLeft = inflatePoint(this.bottomLeft);

    return new Region({
      topLeft: newTopLeft,
      topRight: newTopRight,
      bottomRight: newBottomRight,
      bottomLeft: newBottomLeft,
    });
  }

  contains(x, y) {
    const points = [this.topLeft, this.topRight, this.bottomRight, this.bottomLeft];

    return isInside(new Point(x, y), points);
  }

  pointInside(region) {
    if (region instanceof Rect) {
      return [this.topLeft, this.topRight, this.bottomRight, this.bottomLeft].some((point) =>
        region.contains(point.x, point.y)
      );
    } else if (region instanceof Region) {
      return [this.topLeft, this.topRight, this.bottomRight, this.bottomLeft].some((point) =>
        region.contains(point.x, point.y)
      );
    } else {
      return false;
    }
  }

  union(other) {
    // Convert other to Region if it's a Rect
    let otherRegion;
    if (other instanceof Rect) {
      otherRegion = Region.fromRect(other);
    } else if (other instanceof Region) {
      otherRegion = other;
    } else {
      throw new Error('union() requires a Rect or Region instance');
    }

    // Get all points from both regions
    const allPoints = [
      this.topLeft,
      this.topRight,
      this.bottomRight,
      this.bottomLeft,
      otherRegion.topLeft,
      otherRegion.topRight,
      otherRegion.bottomRight,
      otherRegion.bottomLeft,
    ];

    // Find the bounding box of all points
    let minX = allPoints[0].x;
    let maxX = allPoints[0].x;
    let minY = allPoints[0].y;
    let maxY = allPoints[0].y;

    for (const point of allPoints) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }

    // Return a new Region that represents the union as an axis-aligned rectangle
    return Region.fromRect(new Rect(minX, minY, maxX - minX, maxY - minY));
  }

  rotate(degrees) {
    if (!degrees) return this;
    const centerX = this.center.x;
    const centerY = this.center.y;
    const angle = (Math.PI / 180) * degrees;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const rotatePoint = (point) => {
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const rotatedX = centerX + dx * cos - dy * sin;
      const rotatedY = centerY + dx * sin + dy * cos;
      return new Point(rotatedX, rotatedY);
    };

    const newTopLeft = rotatePoint(this.topLeft);
    const newTopRight = rotatePoint(this.topRight);
    const newBottomRight = rotatePoint(this.bottomRight);
    const newBottomLeft = rotatePoint(this.bottomLeft);

    return new Region({
      topLeft: newTopLeft,
      topRight: newTopRight,
      bottomRight: newBottomRight,
      bottomLeft: newBottomLeft,
    });
  }

  rotateByPoint(centerX, centerY, degrees) {
    if (!degrees) return this;
    const angle = (Math.PI / 180) * degrees;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const rotatePoint = (point) => {
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const rotatedX = centerX + dx * cos - dy * sin;
      const rotatedY = centerY + dx * sin + dy * cos;
      return new Point(rotatedX, rotatedY);
    };

    const newTopLeft = rotatePoint(this.topLeft);
    const newTopRight = rotatePoint(this.topRight);
    const newBottomRight = rotatePoint(this.bottomRight);
    const newBottomLeft = rotatePoint(this.bottomLeft);

    return new Region({
      topLeft: newTopLeft,
      topRight: newTopRight,
      bottomRight: newBottomRight,
      bottomLeft: newBottomLeft,
    });
  }

  /**
   * 应用 6 元素仿射矩阵到 Region 的四个点，中心点为基准（与渲染一致）
   * @param {Array<number>} matrix - 6 元素仿射矩阵 [a, b, c, d, e, f]
   * @returns {Region}
   */
  transformMatrix(matrix) {
    if (!Array.isArray(matrix) || matrix.length !== 6) return this;

    const [a, b, c, d, e, f] = matrix;

    // 性能优化：如果是单位矩阵，直接返回自身，无需计算
    if (a === 1 && b === 0 && c === 0 && d === 1 && e === 0 && f === 0) {
      return this;
    }

    // 预计算中心点，减少重复计算
    const cx = (this.topLeft.x + this.topRight.x + this.bottomRight.x + this.bottomLeft.x) * 0.25;
    const cy = (this.topLeft.y + this.topRight.y + this.bottomRight.y + this.bottomLeft.y) * 0.25;

    // 内联变换函数，避免函数调用开销
    const transformX = (dx, dy) => a * dx + c * dy + e + cx;
    const transformY = (dx, dy) => b * dx + d * dy + f + cy;

    // 批量计算四个点的变换，减少对象创建
    const tlx = this.topLeft.x - cx;
    const tly = this.topLeft.y - cy;
    const trx = this.topRight.x - cx;
    const try_ = this.topRight.y - cy;
    const brx = this.bottomRight.x - cx;
    const bry = this.bottomRight.y - cy;
    const blx = this.bottomLeft.x - cx;
    const bly = this.bottomLeft.y - cy;

    return new Region({
      topLeft: new Point(transformX(tlx, tly), transformY(tlx, tly)),
      topRight: new Point(transformX(trx, try_), transformY(trx, try_)),
      bottomRight: new Point(transformX(brx, bry), transformY(brx, bry)),
      bottomLeft: new Point(transformX(blx, bly), transformY(blx, bly)),
    });
  }

  /**
   * 仿射变换 Region 的四个点，以指定点为基准应用 6 元素矩阵
   * @param {number} centerX - 基准点 x 坐标
   * @param {number} centerY - 基准点 y 坐标
   * @param {Array<number>} matrix - 6 元素仿射矩阵 [a, b, c, d, e, f]
   * @returns {Region}
   */
  transformMatrixByPoint(centerX, centerY, matrix) {
    if (!Array.isArray(matrix) || matrix.length !== 6) return this;

    const [a, b, c, d, e, f] = matrix;

    // 性能优化：如果是单位矩阵，直接返回自身，无需计算
    if (a === 1 && b === 0 && c === 0 && d === 1 && e === 0 && f === 0) {
      return this;
    }

    // 预计算四个点相对于中心点的偏移量，避免重复计算
    const tlx = this.topLeft.x - centerX;
    const tly = this.topLeft.y - centerY;
    const trx = this.topRight.x - centerX;
    const try_ = this.topRight.y - centerY;
    const brx = this.bottomRight.x - centerX;
    const bry = this.bottomRight.y - centerY;
    const blx = this.bottomLeft.x - centerX;
    const bly = this.bottomLeft.y - centerY;

    // 内联变换计算，避免函数调用开销
    return new Region({
      topLeft: new Point(a * tlx + c * tly + e + centerX, b * tlx + d * tly + f + centerY),
      topRight: new Point(a * trx + c * try_ + e + centerX, b * trx + d * try_ + f + centerY),
      bottomRight: new Point(a * brx + c * bry + e + centerX, b * brx + d * bry + f + centerY),
      bottomLeft: new Point(a * blx + c * bly + e + centerX, b * blx + d * bly + f + centerY),
    });
  }

  transform(scale, translateX, translateY) {
    return new Region({
      topLeft: new Point(this.topLeft.x * scale + translateX, this.topLeft.y * scale + translateY),
      topRight: new Point(
        this.topRight.x * scale + translateX,
        this.topRight.y * scale + translateY
      ),
      bottomRight: new Point(
        this.bottomRight.x * scale + translateX,
        this.bottomRight.y * scale + translateY
      ),
      bottomLeft: new Point(
        this.bottomLeft.x * scale + translateX,
        this.bottomLeft.y * scale + translateY
      ),
    });
  }

  toLocal(point) {
    const ux = this.topRight.x - this.topLeft.x;
    const uy = this.topRight.y - this.topLeft.y;
    const vx = this.bottomLeft.x - this.topLeft.x;
    const vy = this.bottomLeft.y - this.topLeft.y;

    const uLen = Math.hypot(ux, uy);
    const vLen = Math.hypot(vx, vy);

    if (uLen === 0 && vLen === 0) return new Point(0, 0);

    const dx = point.x - this.topLeft.x;
    const dy = point.y - this.topLeft.y;

    const uxn = uLen !== 0 ? ux / uLen : 0;
    const uyn = uLen !== 0 ? uy / uLen : 0;
    const vxn = vLen !== 0 ? vx / vLen : 0;
    const vyn = vLen !== 0 ? vy / vLen : 0;

    const localX = dx * uxn + dy * uyn;
    const localY = dx * vxn + dy * vyn;

    return {
      x: localX,
      y: localY,
    };
  }
}

function isInside(point, polygon) {
  const x = point.x,
    y = point.y;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x,
      yi = polygon[i].y;
    const xj = polygon[j].x,
      yj = polygon[j].y;

    const intersect = yi > y != yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function loadImage(imageUrl, onprogress: (progress: number) => void): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let notifiedNotComputable = false;

    xhr.open('GET', imageUrl, true);
    xhr.responseType = 'arraybuffer';

    xhr.onprogress = function (ev) {
      if (ev.lengthComputable) {
        onprogress((ev.loaded / ev.total) * 100);
      } else {
        if (!notifiedNotComputable) {
          notifiedNotComputable = true;
          onprogress(-1);
        }
      }
    };

    xhr.onloadend = function () {
      if (!xhr.status.toString().match(/^2/)) {
        reject(xhr);
        return;
      }
      const options: BlobPropertyBag = {};
      const headers = xhr.getAllResponseHeaders();
      const m = headers.match(/^Content-Type:\s*(.*?)$/im);

      if (m && m[1]) {
        options.type = m[1];
      }

      resolve(window.URL.createObjectURL(new Blob([this.response], options)));
    };

    xhr.send();
  });
}
